import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../../../providers/Layout.provider';
import { useToken } from '../../../../providers/Token.provider';
import useDataFetchIndicator from '../../../../hooks/useDataFetchIndicator';
import usePostIQ11Complete from '../../../../hooks/useApi/usePostIQ11Complete';
import usePostIFrameEvent from '../../../../hooks/useApi/usePostIFrameEvent';
import { useBank } from '../../../../providers/Bank.provider';
import styles from './BankLogin.module.scss';
import { usePaylink } from '../../../../providers/Paylink.provider';
import usePostPaylinkSetAccount from '../../../../hooks/useApi/usePostPaylinkSetAccount';
import { useFlexiblePayment } from '../../../../providers/FlexiblePayment.provider';
import DemoLogin from './DemoLogin/DemoLogin';
import BankSuccessContext, { BankSuccessCtx } from '../SuccessDialouge/BankSuccessContext';
import BankSucessDialogue from '../SuccessDialouge/BankSuccessDialogue';
import { BankSuccessDialogueHandler, DialogueHandlerProps } from '../SuccessDialouge/types';
import FiservContainer from './Fiserv';

type FC = FunctionComponent;

type EventMessageData = {
  step: string;
  loginId: string;
  accountId: string;
  errorCode: string;
  institution: string;
  requestId: string;
  [key: string]: string;
};
type EventMessage = {
  data: EventMessageData;
};
type TokenProps = {
  accessToken: string;
  identificationNumber: string;
  phoneNumber: string;
  fullName: string;
};

const BankLogin: FC = () => {
  const { i18n } = useTranslation('translations', { keyPrefix: 'Common' });
  const { state } = useLocation<TokenProps>();
  const { color } = useLayout();
  const { token, isSetByBrowser } = useToken();
  const { setLayoutInfo } = useLayout();
  const { FinancialInstitution, BankAccountRail } = useBank();
  const [, saveIframeEvent] = usePostIFrameEvent({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [iQ11CompleteResponse, iQ11Complete] = usePostIQ11Complete({});
  const [isIQ11Complete, setIsIQ11Complete] = useState(false);
  const [eventList, setEventList] = useState<EventMessageData[]>([]);
  const [eventInstitution, setEventInstitution] = useState('');
  const { Token } = usePaylink();
  const [paylinkAccountResponse, setPaylinkAccount] = usePostPaylinkSetAccount({});
  const { paymentMethods, paymentType, addPaymentMethod, isFlexiblePayment, partialPaymentValues, installmentDetails } =
    useFlexiblePayment();
  const history = useHistory();
  const [isLinkAccount, setIsLinkAccount] = useState(false);
  const [showConnectBankSuccessAlert, setShowConnectBankSuccessAlert] = useState(false);
  const [retrievediQ11token, setRetrievediQ11token] = useState('');
  const SandBoxBankEnabled = import.meta.env.VITE_SANDBOX_BANK_ENABLED === 'true';

  useEffect(() => {
    const handleMessage = (event: EventMessage) => {
      setEventList((prevEvents) => [...prevEvents, event.data]);
      if (event.data.step === 'APP_MOUNTED') {
        setDataFetchingInfo(false);
      }
      if (event.data.step === 'REDIRECT') {
        setEventInstitution(event.data.institution);
        setIsIQ11Complete(true);
        iQ11Complete({
          institution: event.data.institution,
          loginId: event.data.loginId,
          accountId: event.data.accountId,
          requestId: event.data.requestId,
          FullName: state.fullName,
        });
      }
    };
    window.addEventListener('message', handleMessage, false);
    if (FinancialInstitution?.ExternalFIID !== '0' && token?.Country === 'CA') {
      setDataFetchingInfo(true, ``, 'fullScreen');
    }

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, []);

  useEffect(() => {
    const delayEventSubmission = setTimeout(() => {
      if (eventList.length > 0) {
        saveIframeEvent({
          EventData: JSON.stringify(eventList),
        });
      }
    }, 100);

    return () => clearTimeout(delayEventSubmission);
  }, [eventList]);
  const completePaylink = (iQ11token: string) => {
    if (isFlexiblePayment) {
      if (paymentType === vp.FlexiblePaymentMethods.MultiplePaymentMethods) {
        history.push('/connect/flexible-payment/multiple-payment-methods');
        const paymentMethod = paymentMethods.find((method) => method.method === vp.ConnectionTypes.Bank);
        addPaymentMethod({
          ...paymentMethod,
          amount: paymentMethod?.amount || 0,
          FinancialInstitution,
          token: iQ11token,
          account: '',
          method: vp.ConnectionTypes.Bank,
        });
      }
      if (paymentType === vp.FlexiblePaymentMethods.PartialPayment) {
        setIsLinkAccount(true);
        setPaylinkAccount({
          SingleUseToken: Token,
          Iq11Token: iQ11token,
          PaymentMethod: 'bank',
          FlexiblePaymentOptions: {
            PaymentOption: 'partial',
            PartialPaymentDetails: partialPaymentValues,
          },
        });
      }
      if (paymentType === vp.FlexiblePaymentMethods.InstallmentPlan) {
        setIsLinkAccount(true);
        setPaylinkAccount({
          SingleUseToken: Token,
          Iq11Token: iQ11token,
          PaymentMethod: 'bank',
          FlexiblePaymentOptions: {
            PaymentOption: 'installments',
            InstallmentDetails: installmentDetails,
          },
        });
      }
    } else {
      setIsLinkAccount(true);
      setPaylinkAccount({
        SingleUseToken: Token,
        Iq11Token: iQ11token,
        PaymentMethod: 'bank',
        BankAccountRail: BankAccountRail,
      });
    }
  };

  const CompleteLogin = (iQ11token: string) => {
    setRetrievediQ11token(iQ11token);
    const redirectMethod = token?.RedirectMethod;
    const url = `${token?.RedirectURL || ''}?Token=${iQ11token}&PaymentMethod=bank`;
    const Response = {
      Step: 'LINK',
      Institution: eventInstitution === '' ? 'VOPAY TESTING BANK' : eventInstitution,
      Url: url,
      Token: iQ11token,
      BankId: FinancialInstitution?.FinancialInstitutionID || '',
      PaymentMethod: 'bank',
      ConnectionType: 'online',
    };
    window.top?.postMessage(Response, '*');
    if (window.parent && window.parent !== window.top) {
      window.parent.postMessage(Response, '*');
    }

    if (Token) {
      completePaylink(iQ11token);
    } else if (!isSetByBrowser) {
      history.push('/dashboard');
    }
    setTimeout(() => {
      if (isSetByBrowser) {
        if (redirectMethod === 'InnerRedirect') {
          window.location.href = url;

          // setShowConnectBankSuccessAlert(true);
        } else if (redirectMethod === 'OuterRedirect') {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          window.top!.location.href = url;

          // setShowConnectBankSuccessAlert(true);
        } else {
          history.push('/success');
        }
      }
      // redirect to bank listing page.
    }, 300);
  };
  useEffect(() => {
    if (isIQ11Complete === true) {
      setIsIQ11Complete(false);
      CompleteLogin(iQ11CompleteResponse.Token);
    }
  }, [iQ11CompleteResponse]);

  useEffect(() => {
    if (isLinkAccount && paylinkAccountResponse) {
      setIsLinkAccount(false);

      if (paylinkAccountResponse.Success) {
        history.push('/thank-you', {
          paymentMethodType: 'bank',
          paymentMethod: '',
          connectionType: 'online',
        });
      } else {
        history.push('/connect/transaction-error');
      }
    }
  }, [paylinkAccountResponse]);

  // Fiserv
  useEffect(() => {
    const handleMessage = (event: EventMessage) => {
      if (event.data.step === 'FISERV_COMPLETE') {
        setIsIQ11Complete(true);
        setEventInstitution(FinancialInstitution?.FIName || '');
        iQ11Complete({
          institution: FinancialInstitution?.ExternalFIID === '0' ? 'vopay' : FinancialInstitution?.ExternalFIID || '',
          loginId: event.data.fiLoginAcctId,
          accountId: event.data.acctId,
          IdentificationNumber: state.identificationNumber,
          PhoneNumber: state.phoneNumber,
          SessionToken: state.accessToken,
          FullName: state.fullName,
        });
      }
      if (event.data.step === 'FISERV_ERROR') {
        if (event.data.errorCode === '303') {
          history.push('/connect/bank');
        } else {
          history.push('/error');
          console.error('error code - error', event.data.errorCode);
        }
      }

      if (event.data.step === 'FISERV_CANCEL') {
        history.push('/connect/bank');
        console.error('error code - cancel', event.data.errorCode);
      }
    };
    window.addEventListener('message', handleMessage, false);

    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, []);

  const hasInstitutionColor =
    FinancialInstitution?.ColorCode !== undefined &&
    FinancialInstitution?.ColorCode !== null &&
    FinancialInstitution?.ColorCode !== '';

  useEffect(() => {
    setLayoutInfo(
      vp.chooseColor<string | null | undefined>(
        '#FFD147',
        hasInstitutionColor ? `#${FinancialInstitution?.ColorCode || color}` : null,
        color,
        token?.BrandingDetails?.Branding?.PrimaryColor,
      ),
      'moon',
      '',
      80,
      15,
      0,
      true,
      true,
    );
  }, []);
  const BankSuccessValues: BankSuccessCtx = {
    showConnectBankSuccessAlert,
  };
  const handlers = {
    handleSuccessDialogue: (handlerProps: DialogueHandlerProps<BankSuccessDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: BankSuccessDialogueHandler = {
        create: {
          success: {
            default: () => {
              const redirectMethod = token?.RedirectMethod;
              const url = `${token?.RedirectURL || ''}?Token=${retrievediQ11token}&PaymentMethod=bank`;
              if (redirectMethod === 'InnerRedirect') {
                window.location.href = url;
              } else if (redirectMethod === 'OuterRedirect') {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                window.top!.location.href = url;
              }
              setShowConnectBankSuccessAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };
  // eslint-disable-next-line no-nested-ternary
  const withTransactions = token?.FlinksInstitutionID
    ? '&withTransactions=true&daysOfTransactions=Days365&scheduleRefresh=true'
    : token?.WithTransactions
      ? '&withTransactions=true&daysOfTransactions=Days365'
      : '&withTransactions=true&daysOfTransactions=Days365';

  const flinskFrameUrl = getFlinksFrameURL(FinancialInstitution?.Country || 'CA', !SandBoxBankEnabled);

  const url = `${flinskFrameUrl}Credential/${
    FinancialInstitution?.ExternalFIID || ''
  }?accountSelectorEnable=true&consentEnable=false&language=${i18n.language}&institutionsLocation=${
    token?.Country || ''
  }&backEnable=false&redirectUrl=${
    token?.RedirectURL || ''
  }&innerRedirect=true&windowContext=parent${withTransactions}`;

  return (
    <>
      {token?.Country === 'US' && (
        <FiservContainer accessToken={state.accessToken} FIID={FinancialInstitution?.ExternalFIID} />
      )}
      {FinancialInstitution?.ExternalFIID !== '0' && token?.Country === 'CA' && (
        <div className={styles.container}>
          <iframe className={styles.BankFrame} title="Bank Login" src={url} seamless />
        </div>
      )}
      {FinancialInstitution?.ExternalFIID === '0' && token?.Country === 'CA' && (
        <div className={styles.container}>
          <DemoLogin Finish={CompleteLogin} />
        </div>
      )}
      <BankSuccessContext.Provider value={BankSuccessValues}>
        <BankSucessDialogue handleDialogue={handlers.handleSuccessDialogue} />
      </BankSuccessContext.Provider>
    </>
  );
  function getFlinksFrameURL(Country: string, IsProduction: boolean) {
    if (IsProduction && Country === 'US') {
      return 'https://vopay-us-iframe.private.fin.ag/v2/';
    }
    if (!IsProduction && Country === 'US') {
      return 'https://vopay-us-dev-iframe.private.fin.ag/v2/';
    }
    if (IsProduction && Country === 'CA') {
      return 'https://vopay-iframe.private.fin.ag/v2/';
    }
    return 'https://vopaydev-iframe.private.fin.ag/v2/';
  }
};

export default BankLogin;
